import {
  ADD_CLIENT_SUCCESS,
  ADD_COMPANY_SUCCESS,
  Company,
  UPDATE_CLIENTS_SUCCESS,
  DELETE_COMPANIES_SUCCESS,
} from 'src/store/clients/types';
import { appAPI, ApiTags, ApiMethods } from '.';
import { Client } from 'src/constants';
import { RootState } from 'src/store';
import { MODIFY_INTERNAL_USERS } from 'src/store/users/types';
import {
  addCompanyToClients,
  removeCompanyFromClients,
} from 'src/store/clients/actions';
import { addUpdate } from 'src/store/optimisticUpdates/slice';

type getCompaniesInput = {
  ignoreAccess?: boolean;
};

type ClientInput = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};
type CompanyInput = {
  name: string;
  clients: Array<ClientInput>;
};

export type UnprocessedClientResponse = {
  client: ClientInput;
  error: string;
};

type CreateCompanyResponse = {
  company: Company;
  createdClients: Client[];
  updatedClients: Client[];
  unprocessedClients: Array<UnprocessedClientResponse>;
};

export const companiesApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createCompany: build.mutation<CreateCompanyResponse, CompanyInput>({
      query: (newCompany) => ({
        path: `/v1/companies`,
        method: ApiMethods.post,
        options: {
          body: newCompany,
        },
      }),
      invalidatesTags: [
        ApiTags.companies,
        ApiTags.clientCompanies,
        ApiTags.clientUsers,
      ],
      async onQueryStarted(
        companyInput,
        { dispatch, queryFulfilled, getState },
      ) {
        const state = getState() as RootState;
        const currentUserId = state.user.id;
        const { internalUsers } = state.users;

        // Create optimistic company for immediate UI update
        const optimisticCompany: Company = {
          id: `temp-${Date.now()}`, // Temporary ID
          fields: {
            name: companyInput.name,
            isPlaceholder: false,
            fallbackColor: '#938EAB', // Default fallback color
            avatarImageURL: '',
            primaryDomain: '',
            associatedDomains: [],
            customerId: '',
          },
          ownerId: currentUserId || '',
          identityId: `temp-identity-${Date.now()}`,
          owner: null,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
          entityStatus: 'ACTIVE',
          getstreamId: `temp-getstream-${Date.now()}`,
          metadata: {},
        };

        // Optimistically add the company to the store
        dispatch({ type: ADD_COMPANY_SUCCESS, payload: [optimisticCompany] });

        // Optimistically update internal user's company access
        dispatch({
          type: MODIFY_INTERNAL_USERS,
          payload: internalUsers.map((user) => {
            if (user.id === currentUserId) {
              return {
                ...user,
                companyAccessList: [
                  ...(user.companyAccessList ?? []),
                  optimisticCompany.id,
                ],
              };
            }
            return user;
          }),
        });

        // Optimistically update client's company associations for existing clients
        const existingClientIds = companyInput.clients
          .filter((client) => client.id)
          .map((client) => client.id!);

        if (existingClientIds.length > 0) {
          dispatch(
            addCompanyToClients(existingClientIds, optimisticCompany.id),
          );
        }

        try {
          const { data } = await queryFulfilled;

          // Replace optimistic company with real company data
          dispatch({ type: ADD_COMPANY_SUCCESS, payload: [data.company] });

          // Update client associations with real company ID
          if (existingClientIds.length > 0) {
            // Remove the optimistic association
            dispatch(
              removeCompanyFromClients(existingClientIds, optimisticCompany.id),
            );
            // Add the real association
            dispatch(addCompanyToClients(existingClientIds, data.company.id));
          }

          // Handle created and updated clients
          if (data.createdClients.length) {
            dispatch({
              type: ADD_CLIENT_SUCCESS,
              payload: data.createdClients,
            });
          }
          if (data.updatedClients.length) {
            dispatch({
              type: UPDATE_CLIENTS_SUCCESS,
              payload: data.updatedClients,
            });
          }

          // Update internal users company access
          dispatch({
            type: MODIFY_INTERNAL_USERS,
            payload: internalUsers.map((user) => {
              if (user.id === currentUserId) {
                return {
                  ...user,
                  companyAccessList: [
                    ...(user.companyAccessList ?? []).filter(
                      (c) => c !== optimisticCompany.id,
                    ),
                    data.company.id,
                  ],
                };
              }
              return user;
            }),
          });
        } catch (e) {
          // Revert optimistic updates on error
          if (existingClientIds.length > 0) {
            dispatch(
              removeCompanyFromClients(existingClientIds, optimisticCompany.id),
            );
          }
          // Remove the optimistic company from the store
          dispatch({
            type: DELETE_COMPANIES_SUCCESS,
            payload: [optimisticCompany.id],
          });
          // Revert internal user's company access
          dispatch({
            type: MODIFY_INTERNAL_USERS,
            payload: internalUsers.map((user) => {
              if (user.id === currentUserId) {
                return {
                  ...user,
                  companyAccessList: (user.companyAccessList ?? []).filter(
                    (c) => c !== optimisticCompany.id,
                  ),
                };
              }
              return user;
            }),
          });
        }
      },
    }),
    getCompanies: build.query<Company[], getCompaniesInput | undefined>({
      query: (getCompaniesInput) => ({
        path: `/v1/companies`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: getCompaniesInput,
        },
      }),
      providesTags: [ApiTags.clientCompanies],
    }),
    manageCompanyClients: build.mutation<
      {
        addedClientIds: string[];
        addClientErrors: string[];
        removedClientIds: string[];
        removeClientErrors: string[];
      },
      { companyId: string; addClientIds: string[]; removeClientIds: string[] }
    >({
      query: ({ companyId, addClientIds, removeClientIds }) => ({
        path: `/v1/companies/${companyId}/clients`,
        method: ApiMethods.post,
        options: { body: { addClientIds, removeClientIds } },
      }),
      async onQueryStarted(
        { addClientIds, removeClientIds, companyId },
        { dispatch, queryFulfilled },
      ) {
        if (addClientIds.length > 0) {
          dispatch(
            addUpdate({
              entityType: 'CLIENT_USER',
              itemId: addClientIds?.at(0) ?? '',
            }),
          );
          dispatch(addCompanyToClients(addClientIds, companyId));
        }
        if (removeClientIds.length > 0) {
          dispatch(
            addUpdate({
              entityType: 'CLIENT_USER',
              itemId: removeClientIds?.at(0) ?? '',
            }),
          );
          dispatch(removeCompanyFromClients(removeClientIds, companyId));
        }

        const updateClient = (
          clientIds: string[],
          action: 'add' | 'remove',
        ) => {
          if (action === 'add') {
            dispatch(addCompanyToClients(clientIds, companyId));
          } else {
            dispatch(removeCompanyFromClients(clientIds, companyId));
          }
        };

        // update the client state with response payload.
        // the response contains the ids of the added/removed clients.
        // if the client update is successful, we're good since the state is updated optimistically.
        // if the client update is not successful, we revert the optimistically updated client state. Unfortunatley,
        // we cannot leverage the rtk patch undo function as the clients state is not stored in the rtk query cache.
        try {
          const response = await queryFulfilled;
          if (response.data.addClientErrors.length > 0) {
            // remove the errored clients from the state
            updateClient(response.data.addClientErrors, 'remove');
          }

          if (response.data.removeClientErrors.length > 0) {
            // add back the clients that were removed but errored
            updateClient(response.data.removeClientErrors, 'add');
          }
        } catch (e) {
          // revert the optimistically updated client state.
          if (addClientIds.length > 0) {
            dispatch(removeCompanyFromClients(addClientIds, companyId));
          }
          if (removeClientIds.length > 0) {
            dispatch(addCompanyToClients(removeClientIds, companyId));
          }
        }
      },
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useManageCompanyClientsMutation,
} = companiesApi;
